<template>
  <div
    class="MyMapOsmCategory__maps"
    :class="{ 'MyMapOsmCategory__maps--expanded': !hideDomainDetails }"
  >
    <div class="MyMapOsmCategory__header" @click="toggleDomainDetails">
      <div class="MyMapOsmCategory__headerText">
        <div class="MyMapOsmCategory__title font-weight-bold">{{ categoryName }}</div>
        <div class="MyMapOsmCategory__metaRow">
          <span class="MyMapOsmCategory__meta">{{ totalSousDomainesLabel }}</span>
          <!-- Active-layers badge: kept visible even when the domaine is collapsed, so the user still
               sees at a glance how many of its layers are currently shown on the map. -->
          <span
            v-if="totalActiveLayersCount > 0"
            class="MyMapOsmCategory__activeBadge"
            :title="t('myMap.osmCategories.activeLayersCount', 'Couches actives sur la carte')"
          >
            <Icon icon="mdi:eye" :size="11" />
            {{ totalActiveLayersCount }}
          </span>
        </div>
      </div>
      <Icon
        icon="mdi:chevron-down"
        :class="!hideDomainDetails ? 'MyMapOsmCategory__chevron MyMapOsmCategory__chevron--expanded' : 'MyMapOsmCategory__chevron'"
        :size="20"
      />
    </div>

    <div v-if="!hideDomainDetails" class="MyMapOsmCategory__detailsPanel">
      <div v-if="sortedSousDomaines.length > 0" class="MyMapOsmCategory__sousDomaines">
        <div
          v-for="sousDomaine in sortedSousDomaines"
          :key="getSousDomaineKey(sousDomaine)"
          class="MyMapOsmCategory__sousDomaineBlock"
        >
          <div
            class="MyMapOsmCategory__sousDomaineRow"
            :class="{ 'MyMapOsmCategory__sousDomaineRow--active': isSousDomaineActive(sousDomaine) }"
            @click="setActiveSousDomaine(sousDomaine)"
          >
            <button
              class="MyMapOsmCategory__sousDomaineSelect"
              type="button"
              :title="getSousDomaineDisplayLabel(sousDomaine)"
            >
              <span class="MyMapOsmCategory__sousDomaineName">{{ getSousDomaineDisplayLabel(sousDomaine) }}</span>
            </button>
            <div class="MyMapOsmCategory__sousDomaineActions">
              <!-- Active-layers badge: how many of this sous-domaine's layers are shown on the map right
                   now, kept visible even while the sous-domaine block itself is collapsed. -->
              <span
                v-if="getActiveLayersCountForSousDomaine(sousDomaine) > 0"
                class="MyMapOsmCategory__sousDomaineActiveBadge"
                :title="t('myMap.osmCategories.activeLayersCount', 'Couches actives sur la carte')"
              >
                <Icon icon="mdi:eye" :size="10" />
                {{ getActiveLayersCountForSousDomaine(sousDomaine) }}
              </span>
              <span class="MyMapOsmCategory__sousDomaineCount">{{ getSousDomaineDataCount(sousDomaine) }}</span>
              <button
                v-if="canToggleSousDomaineVisibility(sousDomaine)"
                class="MyMapOsmCategory__sousDomaineEyeBtn"
                type="button"
                :title="
                  isSousDomaineDataVisible(sousDomaine)
                    ? t('myMap.osmCategories.hideAll', 'Masquer les données du sous-domaine')
                    : t('myMap.osmCategories.showAll', 'Afficher les données du sous-domaine')
                "
                @click.stop="toggleSousDomaineDataVisibility(sousDomaine)"
              >
                <Icon
                  :icon="isSousDomaineDataVisible(sousDomaine) ? 'mdi:eye' : 'mdi:eye-off'"
                  color="main-blue"
                  :size="20"
                />
              </button>
            </div>
          </div>

          <div
            v-if="isSousDomaineActive(sousDomaine)"
            class="MyMapOsmCategory__nestedDonnees"
          >
            <!-- ── New format : typeOuvrages with MyMapLayerPicker ── -->
            <template v-if="sousDomaineHasNewFormat(sousDomaine)">
              <div
                v-if="getSousDomaineLayers(sousDomaine).length === 0"
                class="MyMapOsmCategory__emptyState"
              >
                {{ t('common.noData') }}
              </div>
              <div v-else>
                <div
                  v-for="donneeLayer in getSousDomaineLayers(sousDomaine)"
                  :key="donneeLayer.donnee.styleId"
                  class="MyMapOsmCategory__typeOuvrageWrapper"
                >
                  <MyMapLayerPicker
                    v-model:main-layer="donneeLayer.mainLayer"
                    v-model:sub-layers="donneeLayer.subLayers"
                    @update="() => refreshDonneeLayer(donneeLayer)"
                    :loaded-layer-type="LayerType.OSM_LAYER"
                    :category-color="undefined"
                    :count-label="donneeLayer.donnee.count ?? 0"
                  >
                    <template #extra-action>
                      <button
                        class="MyMapOsmCategory__infraMapEyeBtn"
                        type="button"
                        :disabled="isTypeOuvrageStyleUnavailable(donneeLayer.donnee.id as number, sousDomaine)"
                        :title="
                          isTypeOuvrageStyleUnavailable(donneeLayer.donnee.id as number, sousDomaine)
                            ? t('myMap.osmCategories.styleUnavailable')
                            : getIsTypeOuvrageVisibleOnMap(donneeLayer.donnee.id as number, sousDomaine)
                              ? t('myMap.osmCategories.hideInfrastructures')
                              : t('myMap.osmCategories.showInfrastructures')
                        "
                        @click.stop="toggleTypeOuvrageOnMap(donneeLayer.donnee.id as number, sousDomaine)"
                      >
                        <Icon
                          :icon="
                            getIsTypeOuvrageVisibleOnMap(donneeLayer.donnee.id as number, sousDomaine)
                              ? 'mdi:eye'
                              : 'mdi:eye-off'
                          "
                          :size="18"
                          :color="getIsTypeOuvrageVisibleOnMap(donneeLayer.donnee.id as number, sousDomaine) ? 'main-blue' : undefined"
                        />
                      </button>
                    </template>
                  </MyMapLayerPicker>

                  <!-- ── Classification legend/filter (e.g. classe de route) ── -->
                  <div
                    v-if="
                      getClassificationForDonnee(donneeLayer.donnee) &&
                      isClassificationAvailable(donneeLayer.donnee) &&
                      isClassificationExpanded(donneeLayer.donnee.styleId)
                    "
                    class="MyMapOsmCategory__classification"
                  >
                    <div class="MyMapOsmCategory__classificationList">
                      <label
                        v-for="value in getClassificationForDonnee(donneeLayer.donnee)!.values"
                        :key="value.value"
                        class="MyMapOsmCategory__classificationItem"
                      >
                        <input
                          type="checkbox"
                          class="MyMapOsmCategory__classificationCheckbox"
                          :checked="isClassificationValueSelected(donneeLayer.donnee.styleId, value.value)"
                          @change="toggleClassificationValue(donneeLayer.donnee, value.value)"
                        />
                        <span
                          class="MyMapOsmCategory__classificationSwatch"
                          :style="{ backgroundColor: value.color }"
                        ></span>
                        <span class="MyMapOsmCategory__classificationLabel">{{ value.label }}</span>
                        <span
                          v-if="getClassificationValueCount(donneeLayer.donnee, value.value) !== undefined"
                          class="MyMapOsmCategory__classificationCount"
                        >
                          {{ getClassificationValueCount(donneeLayer.donnee, value.value) }}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- ── Old format : MyMapLayerPicker (backward compat) ── -->
            <template v-else>
              <div
                v-if="isSousDomaineLoading(sousDomaine)"
                class="MyMapOsmCategory__emptyState"
              >
                {{ t('common.loading', 'Chargement...') }}
              </div>
              <div
                v-else-if="hasSousDomaineNoData(sousDomaine)"
                class="MyMapOsmCategory__emptyState"
              >
                {{ t('myMap.osmCategories.noDataCommune') }}
              </div>
              <MyMapLayerPicker
                v-else
                v-for="(donneeLayer, index) in getDisplayedDonneeLayersForSousDomaine(sousDomaine)"
                :key="donneeLayer.donnee.styleId"
                :class="index > 0 ? 'mt-1' : ''"
                v-model:main-layer="donneeLayer.mainLayer"
                v-model:sub-layers="donneeLayer.subLayers"
                @update="() => refreshDonneeLayer(donneeLayer)"
                :loaded-layer-type="LayerType.OSM_LAYER"
                :category-color="undefined"
                :data-source-label="donneeSourceAttribution(donneeLayer.donnee)"
              />
            </template>
          </div>
        </div>
      </div>

      <MyMapLayerPicker
        v-for="fondEntry in fondLayerEntries"
        :key="fondEntry.mainLayer.id"
        class="mt-3"
        v-model:main-layer="fondEntry.mainLayer"
        v-model:sub-layers="fondEntry.subLayers"
        @update="() => refreshFondLayer(fondEntry)"
        :loaded-layer-type="LayerType.OSM_LAYER"
        :data-source-label="t('myMap.osmCategories.sourceExpertiseFrance')"
      />

      <template v-if="sortedSousDomaines.length === 0">
        <MyMapLayerPicker
          v-for="(donneeLayer, index) in displayedDonneeLayers"
          :key="donneeLayer.donnee.styleId"
          :class="fondLayerEntries.length > 0 || index > 0 ? 'mt-1' : 'mt-3'"
          v-model:main-layer="donneeLayer.mainLayer"
          v-model:sub-layers="donneeLayer.subLayers"
          @update="() => refreshDonneeLayer(donneeLayer)"
          :loaded-layer-type="LayerType.OSM_LAYER"
          :category-color="undefined"
          :data-source-label="donneeSourceAttribution(donneeLayer.donnee)"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LayerType } from '@/models/enums/geo/LayerType'
import type {
  AtlasCategory,
  AtlasDonnee,
  AtlasSousDomaine,
  AtlasTypeOuvrage,
} from '@/models/interfaces/map/AtlasCategory'
import type { AtlasCategoryFondLayer } from '@/models/interfaces/map/AtlasCategory'
import type { Classification, TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { useMyMapStore } from '@/stores/myMapStore'
import { fondMatchesCategory } from '@/services/map/MapStylesService'
import { formatDomaineLib, formatSousDomaineLib } from '@/services/utils/UtilsService'
import { libelleDynamique } from '@/utils/libelleDynamique'
import { MartinStyleService } from '@/services/map/MartinStyleService'
import { AtlasCategoriesService } from '@/services/map/osm/AtlasCategoriesService'
import { OtherCommuneDataService, OTHER_DATA_CATEGORY_CODE } from '@/services/map/osm/OtherCommuneDataService'
import MyMapLayerPicker from '@/views/map/components/MyMapLayerPicker.vue'
import Icon from '@/components/ui/Icon.vue'
import { computed, type Ref, ref, onMounted, watch } from 'vue'
import { i18n } from '@/plugins/i18n'
import type { Layer } from '@/models/interfaces/map/Layer'
import LayerService from '@/services/map/LayerService'

const props = defineProps<{
  category: AtlasCategory
  expanded?: boolean
  /** Sous-domaine to auto-expand (e.g. from a search result targeting it or a nested type d'ouvrage/donnée). */
  initialSousDomaineId?: number | string | null
}>()

const emits = defineEmits<{
  toggle: [expanded: boolean]
}>()

const myMapStore = useMyMapStore()

function t(key: string, fallback?: string, params?: Record<string, unknown>): string {
  const translated = i18n.t(key, params as never)
  const text = String(translated)
  if (text === key && fallback) {
    return fallback
  }
  return text
}

interface DonneeLayer {
  donnee: AtlasDonnee
  mainLayer: Layer
  subLayers: Layer[]
}

interface FondLayerEntry {
  styleId: string
  mainLayer: Layer
  subLayers: Layer[]
}

const donneeLayers: Ref<DonneeLayer[]> = ref([])
const fondLayerEntries: Ref<FondLayerEntry[]> = ref([])
const activeSousDomaineKey: Ref<string | null> = ref(null)
const sousDomaineDataVisibility: Ref<Record<string, boolean>> = ref({})
const loadedOtherDataBySousDomaine: Ref<Record<string, AtlasDonnee[]>> = ref({})
const loadingOtherDataBySousDomaine: Ref<Record<string, boolean>> = ref({})
const loadedOtherDataKeys: Ref<Record<string, boolean>> = ref({})
const hideDomainDetails: Ref<boolean> = ref(!(props.expanded ?? false))

watch(
  () => props.expanded,
  (value) => {
    if (typeof value === 'boolean') {
      hideDomainDetails.value = !value
    }
  },
)

watch(
  () => props.initialSousDomaineId,
  (id) => {
    if (id == null) return
    const key = String(id)
    const sousDomaine = sortedSousDomaines.value.find((sd) => getSousDomaineKey(sd) === key)
    if (!sousDomaine) return
    activeSousDomaineKey.value = key
    void ensureSousDomaineDataLoaded(sousDomaine)
  },
)

const categoryName = computed(() => formatDomaineLib(props.category.atlas_category ?? props.category.name))
const allowedOsmStyleIds = computed(() => myMapStore.allowedOsmStyleIds)
const isOtherDataCategory = computed(() => props.category.code === OTHER_DATA_CATEGORY_CODE)

const effectiveCategoryDonnees = computed(() => {
  if (!isOtherDataCategory.value) {
    return props.category.donnees ?? []
  }
  return sortedSousDomaines.value.flatMap((sousDomaine) => getSousDomaineDonnees(sousDomaine))
})

function normalizeAttributionLabel(value: string): string {
  return value.trim().toLowerCase()
}

function getDonneeLabel(donnee: AtlasDonnee): string {
  // `name` porte le libellé français : le prendre en premier laissait l'arborescence en français
  // même en anglais. libelleDynamique choisit selon la langue et retombe sur le français.
  const traduit = libelleDynamique(donnee.typeOuvrageLib ?? donnee.nameFr ?? donnee.name, donnee.nameEn)
  return traduit || donnee.code || String(donnee.id)
}

function getSousDomaineLabel(sousDomaine: AtlasSousDomaine): string {
  const traduit = libelleDynamique(sousDomaine.nameFr ?? sousDomaine.name, sousDomaine.nameEn)
  return traduit || sousDomaine.code || String(sousDomaine.id)
}

/** Display-only label: maps the backend sous-domaine name to its short index label for the right panel. */
function getSousDomaineDisplayLabel(sousDomaine: AtlasSousDomaine): string {
  return formatSousDomaineLib(getSousDomaineLabel(sousDomaine))
}

// donnees is already deduped by typId in AtlasCategoriesService (a typeOuvrage can belong to
// several compétences, so counting competenceDonnees[].typeOuvrages directly would double-count).
function getSousDomaineDataCount(sousDomaine: AtlasSousDomaine): number {
  return getSousDomaineDonnees(sousDomaine).filter((donnee) => donnee.hasData !== false).length
}

function getSousDomaineKey(sousDomaine: AtlasSousDomaine): string {
  return String(sousDomaine.id)
}

function getDonneeKey(donnee: AtlasDonnee): string {
  return donnee.styleId || String(donnee.id)
}

function isSousDomaineActive(sousDomaine: AtlasSousDomaine): boolean {
  return activeSousDomaineKey.value === getSousDomaineKey(sousDomaine)
}

/**
 * Counts this sous-domaine's layers that are currently shown on the map (mainLayer.isShown === true),
 * regardless of whether the sous-domaine block itself is expanded or collapsed. Powers the small
 * badge next to the sous-domaine name so active layers stay visible even once collapsed.
 */
function getActiveLayersCountForSousDomaine(sousDomaine: AtlasSousDomaine): number {
  return getSousDomaineLayers(sousDomaine).filter((layer) => layer.mainLayer.isShown === true).length
}

function isSousDomaineDataVisible(sousDomaine: AtlasSousDomaine): boolean {
  const key = getSousDomaineKey(sousDomaine)
  return sousDomaineDataVisibility.value[key] === true
}

function setActiveSousDomaine(sousDomaine: AtlasSousDomaine): void {
  const key = getSousDomaineKey(sousDomaine)
  activeSousDomaineKey.value = activeSousDomaineKey.value === key ? null : key
  if (activeSousDomaineKey.value === key) {
    void ensureSousDomaineDataLoaded(sousDomaine)
  }
}

function toggleDomainDetails(): void {
  hideDomainDetails.value = !hideDomainDetails.value
  emits('toggle', !hideDomainDetails.value)
}

function toggleSousDomaineDataVisibility(sousDomaine: AtlasSousDomaine): void {
  const key = getSousDomaineKey(sousDomaine)
  const currentlyVisible = sousDomaineDataVisibility.value[key] === true
  const targetVisible = !currentlyVisible

  for (const donneeLayer of getSousDomaineLayers(sousDomaine)) {
    donneeLayer.mainLayer = { ...donneeLayer.mainLayer, isShown: targetVisible }
    myMapStore.setOsmLayerVisible(donneeLayer.donnee.styleId, targetVisible)
  }

  sousDomaineDataVisibility.value = {
    ...sousDomaineDataVisibility.value,
    [key]: targetVisible,
  }
  if (targetVisible) {
    activeSousDomaineKey.value = key
  }
}

function getSousDomaineLayers(sousDomaine: AtlasSousDomaine): DonneeLayer[] {
  return sousDomaineDonneeLayers.value.get(getSousDomaineKey(sousDomaine)) ?? []
}

function getSousDomaineDonnees(sousDomaine: AtlasSousDomaine): AtlasDonnee[] {
  if (!isOtherDataCategory.value) {
    return sousDomaine.donnees ?? []
  }
  return loadedOtherDataBySousDomaine.value[getSousDomaineKey(sousDomaine)] ?? []
}

function canToggleSousDomaineVisibility(sousDomaine: AtlasSousDomaine): boolean {
  if (sousDomaineHasNewFormat(sousDomaine)) return false
  return getSousDomaineDataCount(sousDomaine) > 0
}

function isSousDomaineLoading(sousDomaine: AtlasSousDomaine): boolean {
  return loadingOtherDataBySousDomaine.value[getSousDomaineKey(sousDomaine)] === true
}

function hasSousDomaineNoData(sousDomaine: AtlasSousDomaine): boolean {
  if (!isOtherDataCategory.value) return false
  const key = getSousDomaineKey(sousDomaine)
  return loadedOtherDataKeys.value[key] === true && getSousDomaineDataCount(sousDomaine) === 0
}

async function ensureSousDomaineDataLoaded(sousDomaine: AtlasSousDomaine): Promise<void> {
  if (!isOtherDataCategory.value) return
  const key = getSousDomaineKey(sousDomaine)
  if (loadedOtherDataKeys.value[key] || loadingOtherDataBySousDomaine.value[key]) return

  loadingOtherDataBySousDomaine.value = {
    ...loadingOtherDataBySousDomaine.value,
    [key]: true,
  }

  try {
    const result = await OtherCommuneDataService.getOtherDataByCommune(getSousDomaineLabel(sousDomaine))
    loadedOtherDataBySousDomaine.value = {
      ...loadedOtherDataBySousDomaine.value,
      [key]: result.donnees,
    }
    loadedOtherDataKeys.value = {
      ...loadedOtherDataKeys.value,
      [key]: true,
    }
    loadCategoryLayers()
  } finally {
    loadingOtherDataBySousDomaine.value = {
      ...loadingOtherDataBySousDomaine.value,
      [key]: false,
    }
  }
}

function syncSousDomaineVisibilityFromLayers(): void {
  const nextVisibility = { ...sousDomaineDataVisibility.value }
  for (const sousDomaine of sortedSousDomaines.value) {
    const key = getSousDomaineKey(sousDomaine)
    const layers = getSousDomaineLayers(sousDomaine)
    nextVisibility[key] = layers.length > 0 && layers.every((layer) => layer.mainLayer.isShown === true)
  }
  sousDomaineDataVisibility.value = nextVisibility
}

function isCommunesDonnee(donnee: AtlasDonnee): boolean {
  const frTitle = normalizeAttributionLabel(
    String(i18n.t('myMap.commune.title', 'Couche Communes', { locale: 'fr' })),
  )
  const enTitle = normalizeAttributionLabel(
    String(i18n.t('myMap.commune.title', 'Communes layer', { locale: 'en' })),
  )
  const label = getDonneeLabel(donnee)
  const subFr = normalizeAttributionLabel(label)
  const subEn = normalizeAttributionLabel(label)
  return subFr === frTitle || subFr === enTitle || subEn === frTitle || subEn === enTitle
}

function donneeSourceAttribution(donnee: AtlasDonnee): string {
  if (typeof donnee.source === 'string' && donnee.source.trim().length > 0) {
    return `Source : ${donnee.source.trim()}`
  }
  return isCommunesDonnee(donnee)
    ? t('myMap.osmCategories.sourceExpertiseFrance')
    : t('myMap.osmCategories.sourceOsm')
}

onMounted(() => {
  loadCategoryLayers()
  void prefetchStylesForCategory()
})

watch(
  [() => allowedOsmStyleIds.value, () => props.category, () => myMapStore.fondLayerStyles],
  () => {
    loadCategoryLayers()
    void prefetchStylesForCategory()
  },
  { deep: true },
)

watch(() => myMapStore.osmAppliedStyleData, () => loadCategoryLayers())

watch(
  () => myMapStore.visibleOsmStyleIds,
  (ids) => {
    for (const entry of donneeLayers.value) {
      const styleId = entry.donnee.styleId
      const shouldShow = ids.includes(styleId)
      if (entry.mainLayer.isShown !== shouldShow) {
        entry.mainLayer = { ...entry.mainLayer, isShown: shouldShow }
      }
    }
    syncSousDomaineVisibilityFromLayers()
  },
)

watch(
  () => myMapStore.visibleFondStyleIds,
  (ids) => {
    for (const entry of fondLayerEntries.value) {
      const shouldShow = ids.includes(entry.styleId)
      if (entry.mainLayer.isShown !== shouldShow) {
        entry.mainLayer = { ...entry.mainLayer, isShown: shouldShow }
      }
    }
  },
)

const GEOMETRY_FALLBACK_ICON: Record<string, string> = {
  point: 'mdi:map-marker',
  line: 'mdi:vector-polyline',
  polygon: 'mdi:vector-square',
  roads: 'mdi:road-variant',
}

const DEFAULT_FOND_ICON = 'mdi:layers-outline'

function normalizeIconName(raw: string): string {
  if (raw.includes(':') || raw.startsWith('$') || raw.startsWith('mdi-')) return raw
  if (raw.startsWith('maki-')) return `maki:${raw.slice(5)}`
  return `maki:${raw}`
}

function resolveDonneeIcon(donnee: AtlasDonnee): string {
  if (donnee.icon) return normalizeIconName(donnee.icon)
  if (!donnee.geometryType) return 'mdi:map-marker'
  return GEOMETRY_FALLBACK_ICON[donnee.geometryType] ?? 'mdi:map-marker'
}

function resolveFondIcon(fond: AtlasCategoryFondLayer): string {
  const raw = fond.icon ?? fond.metadata?.icon
  if (raw) return normalizeIconName(raw)
  return DEFAULT_FOND_ICON
}

async function prefetchStylesForCategory(): Promise<void> {
  const LAYER_TYPE_ORDER: ReadonlyArray<string> = ['symbol', 'fill', 'line', 'circle']

  const subcategoryStyleIds = effectiveCategoryDonnees.value
    .filter((donnee) => donnee.hasData !== false)
    .map((donnee) => donnee.styleId)
    .filter((styleId) => allowedOsmStyleIds.value.includes(styleId))
    .filter((styleId) => !myMapStore.osmAppliedStyleData.has(styleId))

  await Promise.allSettled(
    subcategoryStyleIds.map(async (styleId) => {
      const style = await MartinStyleService.loadStyleFromMapStyles(styleId)
      if (!style?.layers) return
      if (myMapStore.osmAppliedStyleData.has(styleId)) return

      const firstLayer = LAYER_TYPE_ORDER.reduce<
        (typeof style.layers)[number] | undefined
      >((found, type) => found ?? style.layers!.find((l) => l.type === type), undefined)

      if (!firstLayer) return
      const layout =
        'layout' in firstLayer ? (firstLayer.layout as Record<string, unknown>) : {}
      const paint =
        'paint' in firstLayer ? (firstLayer.paint as Record<string, unknown>) : {}
      const rawIconName = layout?.['icon-image']
      const symbolLayer = style.layers!.find((l) => l.type === 'symbol')
      const symbolMeta = symbolLayer
        ? ((symbolLayer as unknown as Record<string, unknown>).metadata as Record<string, unknown> | undefined)
        : undefined
      const rawCustomIconUrl = symbolMeta?.['sigcom:mapIconContentUrl']
      myMapStore.setOsmAppliedStyleData(styleId, {
        iconName: typeof rawIconName === 'string' && rawIconName ? rawIconName : undefined,
        customIconUrl: typeof rawCustomIconUrl === 'string' && rawCustomIconUrl ? rawCustomIconUrl : undefined,
        paint,
        layout,
      })
    }),
  )
}

function loadCategoryLayers() {
  const allowed = new Set(allowedOsmStyleIds.value)
  const baseDonneesWithData = effectiveCategoryDonnees.value.filter((donnee) => donnee.hasData !== false)
  const donneesWithData =
    allowed.size === 0
      ? baseDonneesWithData
      : (() => {
          const matched = baseDonneesWithData.filter((donnee) => allowed.has(donnee.styleId))
          return matched.length > 0 ? matched : baseDonneesWithData
        })()
  const sortedDonnees = [...donneesWithData].sort((a, b) => a.order - b.order)

  donneeLayers.value = sortedDonnees.map((donnee) => {
    const styleId = donnee.styleId
    const isVisible = myMapStore.visibleOsmStyleIds.includes(styleId)

    const applied = myMapStore.osmAppliedStyleData.get(styleId)
    const mainLayer = LayerService.initLayer(
      {
        id: styleId,
        name: getDonneeLabel(donnee),
        icon: resolveDonneeIcon(donnee),
        style: {
          paint: applied?.paint ?? donnee.defaultStyle?.paint,
          layout: applied?.layout ?? donnee.defaultStyle?.layout,
          geometryType: donnee.geometryType,
          iconName: (() => {
            const defaultIcon = donnee.defaultStyle?.layout?.['icon-image']
            return applied?.iconName ?? (typeof defaultIcon === 'string' ? defaultIcon : undefined)
          })(),
          customIconUrl: applied?.customIconUrl,
          // Falls back to the competenceDonnees/typeOuvrages-derived source when this donnee's own
          // markerConfig is empty: `category.donnees` (used above) and the per-sous-domaine
          // typeOuvrages list (see AtlasCategoriesService.normalizeSousDomaine) can diverge when the
          // API also returns a top-level donnees array, and only the latter is guaranteed to carry
          // the type d'ouvrage's marker_config.
          markerConfig: (donnee.markerConfig ??
            AtlasCategoriesService.findMarkerConfigForTypeOuvrageStyleId(styleId) ??
            undefined) as TypeOuvrageMarkerConfig | undefined,
        },
      },
      isVisible,
    )

    return {
      donnee,
      mainLayer,
      subLayers: [],
    }
  })

  loadFondLayers()
  initializeSousDomaineState()
  syncSousDomaineVisibilityFromLayers()
}

function initializeSousDomaineState() {
  const sousDomaines = sortedSousDomaines.value
  const nextVisibility: Record<string, boolean> = {}

  for (const sousDomaine of sousDomaines) {
    const key = getSousDomaineKey(sousDomaine)
    nextVisibility[key] = sousDomaineDataVisibility.value[key] === true
  }
  sousDomaineDataVisibility.value = nextVisibility

  if (sousDomaines.length === 0) {
    activeSousDomaineKey.value = null
    return
  }

  const currentExists = sousDomaines.some((sousDomaine) => getSousDomaineKey(sousDomaine) === activeSousDomaineKey.value)
  if (!currentExists) {
    activeSousDomaineKey.value = null
  }
}

function getFondStyleId(fond: AtlasCategoryFondLayer): string {
  if (fond.styleId) return fond.styleId
  const fondLabel = fond.label ?? fond.name ?? String(fond.id)
  const storeMatch = myMapStore.fondLayerStyles.find(
    (s) => (s.metadata?.label ?? s.id) === fondLabel,
  )
  if (storeMatch) return storeMatch.id
  if (fond.name) return fond.name
  return String(fond.id)
}

function getFondLabel(fond: AtlasCategoryFondLayer): string {
  return fond.label ?? fond.name ?? String(fond.id)
}

function loadFondLayers() {
  const apiFonds = props.category.fondLayers ?? []

  if (apiFonds.length > 0) {
    fondLayerEntries.value = apiFonds.map((fond) => {
      const styleId = getFondStyleId(fond)
      const isVisible = myMapStore.visibleFondStyleIds.includes(styleId)
      const mainLayer = LayerService.initLayer(
        {
          id: styleId,
          name: getFondLabel(fond),
          icon: resolveFondIcon(fond),
        },
        isVisible,
      )
      return { styleId, mainLayer, subLayers: [] }
    })
    return
  }

  const storeFonds = myMapStore.fondLayerStyles.filter((f) =>
    fondMatchesCategory(f, props.category.atlas_category ?? props.category.name),
  )
  fondLayerEntries.value = storeFonds.map((fond) => {
    const isVisible = myMapStore.visibleFondStyleIds.includes(fond.id)
    const mainLayer = LayerService.initLayer(
      {
        id: fond.id,
        name: fond.metadata?.label ?? fond.id,
        icon: DEFAULT_FOND_ICON,
      },
      isVisible,
    )
    return { styleId: fond.id, mainLayer, subLayers: [] }
  })
}

function refreshFondLayer(entry: FondLayerEntry) {
  const isShown = entry.mainLayer.isShown ?? false
  myMapStore.setFondLayerVisible(entry.styleId, isShown)
}

const sortedSousDomaines = computed(() => {
  return [...(props.category.sousDomaines ?? [])].sort((a, b) => a.order - b.order)
})

const totalSousDomainesLabel = computed(() => {
  const count = sortedSousDomaines.value.length
  // Le pluriel était accordé à la main, en français, et restait donc en français dans une
  // interface passée en anglais. vue-i18n choisit la forme depuis le fichier de traduction.
  // i18n.t plutôt que le `t` local de ce composant, dont la signature (clé, repli, params) ne
  // permet pas de passer le nombre qui choisit la forme du pluriel.
  const cle = isOtherDataCategory.value
    ? 'myMap.osmCategories.communesCount'
    : 'myMap.osmCategories.sousDomainesCount'
  return String(i18n.t(cle, { count }, count))
})

/**
 * Total number of layers currently shown on the map for this whole domaine (category): every
 * type d'ouvrage/donnée layer plus every fond layer. `donneeLayers` already aggregates the donnees
 * of every sous-domaine in the category (see `loadCategoryLayers`), so no extra flattening is needed.
 * Badges the domaine header so the count survives collapsing the domaine.
 */
const totalActiveLayersCount = computed(() => {
  const activeDonneeLayers = donneeLayers.value.filter((layer) => layer.mainLayer.isShown === true).length
  const activeFondLayers = fondLayerEntries.value.filter((entry) => entry.mainLayer.isShown === true).length
  return activeDonneeLayers + activeFondLayers
})

/** Catch-all type d'ouvrage label: always shown last within its sous-domaine, after every named type. */
const ORDER_LAST_DONNEE_LABEL = 'autres'

/** Stable sort pushing the "Autres" type d'ouvrage to the end, keeping every other type's relative order. */
function sortDonneeLayersAutresLast(layers: DonneeLayer[]): DonneeLayer[] {
  return [...layers].sort((a, b) => {
    const aLast = getDonneeLabel(a.donnee).trim().toLowerCase() === ORDER_LAST_DONNEE_LABEL ? 1 : 0
    const bLast = getDonneeLabel(b.donnee).trim().toLowerCase() === ORDER_LAST_DONNEE_LABEL ? 1 : 0
    return aLast - bLast
  })
}

const sousDomaineDonneeLayers = computed(() => {
  const layerByDonneeKey = new Map(donneeLayers.value.map((layer) => [getDonneeKey(layer.donnee), layer]))
  const result = new Map<string, DonneeLayer[]>()

  for (const sousDomaine of sortedSousDomaines.value) {
    const key = getSousDomaineKey(sousDomaine)
    const relatedLayers: DonneeLayer[] = []

    for (const donnee of getSousDomaineDonnees(sousDomaine)) {
      const layer = layerByDonneeKey.get(getDonneeKey(donnee))
      if (layer) {
        relatedLayers.push(layer)
      }
    }

    result.set(key, sortDonneeLayersAutresLast(relatedLayers))
  }

  return result
})

const filteredDonneeLayers = computed(() => {
  if (!myMapStore.osmCategorySearchText) {
    return donneeLayers.value
  }

  const search = myMapStore.osmCategorySearchText.toLowerCase()
  return donneeLayers.value.filter((donneeLayer) => {
    const name = getDonneeLabel(donneeLayer.donnee)
    return name.toLowerCase().includes(search)
  })
})

const displayedDonneeLayers = computed(() => {
  if (sortedSousDomaines.value.length === 0) {
    return filteredDonneeLayers.value
  }

  const activeKey = activeSousDomaineKey.value
  if (!activeKey) {
    return []
  }

  const isVisible = sousDomaineDataVisibility.value[activeKey] === true
  if (!isVisible) {
    return []
  }

  const activeLayers = sousDomaineDonneeLayers.value.get(activeKey) ?? []
  if (!myMapStore.osmCategorySearchText) {
    return activeLayers
  }

  const search = myMapStore.osmCategorySearchText.toLowerCase()
  return activeLayers.filter((donneeLayer) =>
    getDonneeLabel(donneeLayer.donnee).toLowerCase().includes(search),
  )
})

function getDisplayedDonneeLayersForSousDomaine(sousDomaine: AtlasSousDomaine): DonneeLayer[] {
  if (!isSousDomaineActive(sousDomaine)) {
    return []
  }

  const layers = getSousDomaineLayers(sousDomaine)
  if (!myMapStore.osmCategorySearchText) {
    return layers
  }

  const search = myMapStore.osmCategorySearchText.toLowerCase()
  return layers.filter((donneeLayer) =>
    getDonneeLabel(donneeLayer.donnee).toLowerCase().includes(search),
  )
}

function refreshDonneeLayer(donneeLayer: DonneeLayer) {
  const isShown = donneeLayer.mainLayer.isShown ?? false
  const styleId = donneeLayer.donnee.styleId
  myMapStore.setOsmLayerVisible(styleId, isShown)
  syncSousDomaineVisibilityFromLayers()
}

// ─── New-format helpers (competenceDonnees / typeOuvrage / infra) ──────────

function sousDomaineHasNewFormat(sousDomaine: AtlasSousDomaine): boolean {
  return (sousDomaine.competenceDonnees?.length ?? 0) > 0
}

function findTypeOuvrage(
  donneeId: number,
  sousDomaine: AtlasSousDomaine,
): AtlasTypeOuvrage | undefined {
  return sousDomaine.competenceDonnees?.flatMap((cd) => cd.typeOuvrages).find((to) => to.typId === donneeId)
}

/** Whether this typeOuvrage's Martin vector tile layer (infra_type_ouvrage_{typId}) is on the map. */
function getIsTypeOuvrageVisibleOnMap(
  donneeId: number,
  sousDomaine: AtlasSousDomaine,
): boolean {
  const typeOuvrage = findTypeOuvrage(donneeId, sousDomaine)
  if (!typeOuvrage) return false
  return myMapStore.visibleOsmStyleIds.includes(typeOuvrage.styleId)
}

/** True once the style failed to load (e.g. 404, typeOuvrage removed from the référentiel): disable the toggle. */
function isTypeOuvrageStyleUnavailable(
  donneeId: number,
  sousDomaine: AtlasSousDomaine,
): boolean {
  const typeOuvrage = findTypeOuvrage(donneeId, sousDomaine)
  if (!typeOuvrage) return false
  return myMapStore.osmStyleLoadFailedIds.has(typeOuvrage.styleId)
}

/**
 * Toggling the eye also drives the classification drawer: showing the layer opens it, hiding the
 * layer closes it, so the classification checkboxes never appear detached from an active layer.
 */
function toggleTypeOuvrageOnMap(
  donneeId: number,
  sousDomaine: AtlasSousDomaine,
): void {
  const typeOuvrage = findTypeOuvrage(donneeId, sousDomaine)
  if (!typeOuvrage) return

  const isCurrentlyVisible = myMapStore.visibleOsmStyleIds.includes(typeOuvrage.styleId)
  const nextVisible = !isCurrentlyVisible
  myMapStore.setOsmLayerVisible(typeOuvrage.styleId, nextVisible)
  setClassificationExpanded(typeOuvrage.styleId, nextVisible)
}

// ─── Classification legend/filter (e.g. classe de route, catégorie de parcelle) ──

/** Default classification axis (classifications[0]) for a donnée, if the backend configured one. */
function getClassificationForDonnee(donnee: AtlasDonnee): Classification | undefined {
  const markerConfig = (donnee.markerConfig ??
    AtlasCategoriesService.findMarkerConfigForTypeOuvrageStyleId(donnee.styleId) ??
    undefined) as TypeOuvrageMarkerConfig | undefined
  return markerConfig?.classifications?.[0]
}

/**
 * False once OsmLayerHandler has confirmed, from the currently loaded tiles, that no feature of
 * this donnée carries the classification key for the active commune (e.g. CATEGORIE missing for
 * schools imported outside the Soa data lot). Keeps the checkboxes hidden in that case instead of
 * offering a filter that would always empty the map — see ISSUE_DATA_CLASSIFICATIONS_TYPE_OUVRAGE.md.
 */
function isClassificationAvailable(donnee: AtlasDonnee): boolean {
  return !myMapStore.osmClassificationUnavailableStyleIds.has(donnee.styleId)
}

/** Feature count for one classification value, tallied client-side by OsmLayerHandler from the
 *  currently loaded tiles. Undefined (not 0) while not yet computed, so the badge stays hidden
 *  instead of flashing a wrong "0" before tiles finish loading. */
function getClassificationValueCount(donnee: AtlasDonnee, value: string): number | undefined {
  return myMapStore.osmClassificationValueCounts.get(donnee.styleId)?.get(value)
}

const expandedClassifications: Ref<Set<string>> = ref(new Set())

function isClassificationExpanded(styleId: string): boolean {
  return expandedClassifications.value.has(styleId)
}

function setClassificationExpanded(styleId: string, expanded: boolean): void {
  const next = new Set(expandedClassifications.value)
  if (expanded) next.add(styleId)
  else next.delete(styleId)
  expandedClassifications.value = next
}

function isClassificationValueSelected(styleId: string, value: string): boolean {
  return myMapStore.osmClassificationFilters.get(styleId)?.values.includes(value) ?? false
}

/** Toggles one class in/out of the selection; an empty selection means "show the whole layer". */
function toggleClassificationValue(donnee: AtlasDonnee, value: string): void {
  const classification = getClassificationForDonnee(donnee)
  if (!classification) return
  const currentValues = myMapStore.osmClassificationFilters.get(donnee.styleId)?.values ?? []
  const nextValues = currentValues.includes(value)
    ? currentValues.filter((v) => v !== value)
    : [...currentValues, value]
  myMapStore.setOsmClassificationFilter(donnee.styleId, classification.key, nextValues)
  // Picking a class implies the user wants to see it on the map: turn the layer on if it
  // was still hidden, otherwise the filter is set but nothing renders (confusing "nothing
  // happens" behavior).
  if (nextValues.length > 0 && !myMapStore.visibleOsmStyleIds.includes(donnee.styleId)) {
    myMapStore.setOsmLayerVisible(donnee.styleId, true)
  }
}
</script>

<style lang="scss">
.MyMapOsmCategory__maps {
  display: flex;
  flex-flow: column nowrap;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  min-width: 0;
  border: 1px solid rgba(var(--v-theme-main-blue), 0.16);
  border-radius: var(--dim-radius);
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.6rem;
  background: linear-gradient(180deg, rgba(var(--v-theme-main-blue), 0.03) 0%, rgba(var(--v-theme-main-blue), 0.015) 100%);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.MyMapOsmCategory__maps--expanded {
  border-color: rgba(var(--v-theme-main-blue), 0.4);
  box-shadow: 0 8px 18px rgba(2, 89, 89, 0.08);
}

.MyMapOsmCategory__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: 0;
  cursor: pointer;
  min-height: var(--panel-row-min-height);
  border-radius: 0.55rem;
  padding: 0.2rem 0.35rem;

  &:hover {
    background: rgba(var(--v-theme-main-blue), 0.06);
  }
}

.MyMapOsmCategory__headerText {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.MyMapOsmCategory__chevron {
  color: rgba(0, 0, 0, 0.54);
  transition: transform 0.2s ease;
}

.MyMapOsmCategory__chevron--expanded {
  transform: rotate(180deg);
}

.MyMapOsmCategory__detailsPanel {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid var(--neutral-border);
}

.MyMapOsmCategory__title {
  font-size: 0.84rem;
  font-weight: 700;
  color: rgb(var(--v-theme-main-blue));
  line-height: 1.25;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.MyMapOsmCategory__meta {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 999px;
  background: rgba(var(--v-theme-main-blue), 0.1);
  color: rgba(var(--v-theme-main-blue), 0.84);
  padding: 0.1rem 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

// Row holding the sous-domaines-count pill and the active-layers badge side by side.
.MyMapOsmCategory__metaRow {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 0.35rem;
}

// Domaine-level "N layers shown on the map" badge: green so it reads as "active" at a glance,
// distinct from the neutral grey sous-domaines-count pill it sits next to.
.MyMapOsmCategory__activeBadge {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  width: fit-content;
  border-radius: 999px;
  background: rgba(var(--v-theme-main-green), 0.14);
  color: rgb(var(--v-theme-main-green));
  padding: 0.1rem 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
}

.MyMapOsmCategory__maps .MyMapLayerPicker:last-child .MyMapLayerPicker__parentBlock {
  border-bottom: none;
}

.MyMapOsmCategory__sousDomaines {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0.1rem 0 0.45rem;
}

.MyMapOsmCategory__sousDomaineBlock {
  display: flex;
  flex-direction: column;
}

.MyMapOsmCategory__sousDomaineRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--neutral-border);
  border-radius: 0.5rem;
  padding: 0.24rem 0.35rem 0.24rem 0.62rem;
  background: rgba(var(--v-theme-main-blue), 0.03);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: rgba(var(--v-theme-main-blue), 0.07);
    border-color: rgba(var(--v-theme-main-blue), 0.36);
  }
}

.MyMapOsmCategory__sousDomaineRow--active {
  border-color: rgba(var(--v-theme-main-blue), 0.45);
  background: rgba(var(--v-theme-main-blue), 0.08);
}

.MyMapOsmCategory__sousDomaineSelect {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  text-align: left;
  background: transparent;
  border: none;
  padding: 0.25rem 0;
  cursor: pointer;
}

.MyMapOsmCategory__sousDomaineActions {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.MyMapOsmCategory__sousDomaineName {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgb(var(--v-theme-main-blue));
  line-height: 1.2;
  padding-right: 0.5rem;
}

.MyMapOsmCategory__sousDomaineCount {
  font-size: 0.72rem;
  color: rgba(var(--v-theme-main-blue), 0.7);
  font-weight: 700;
  min-width: 1.5rem;
  text-align: right;
}

// Sous-domaine-level "N layers shown on the map" badge (same green treatment as the domaine one).
.MyMapOsmCategory__sousDomaineActiveBadge {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  border-radius: 999px;
  background: rgba(var(--v-theme-main-green), 0.14);
  color: rgb(var(--v-theme-main-green));
  padding: 0.05rem 0.4rem;
  font-size: 0.68rem;
  font-weight: 700;
}

.MyMapOsmCategory__sousDomaineEyeBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  cursor: pointer;

  &:hover {
    background: rgba(var(--v-theme-main-blue), 0.1);
  }
}

.MyMapOsmCategory__nestedDonnees {
  margin: 0.25rem 0 0.15rem 0.9rem;
  padding-left: 0.6rem;
  border-left: 2px solid rgba(var(--v-theme-main-blue), 0.14);
}

.MyMapOsmCategory__emptyState {
  padding: 0.6rem 0.4rem;
  color: rgba(var(--v-theme-main-blue), 0.72);
  font-size: 0.78rem;
  line-height: 1.35;
}

// TypeOuvrage wrapper with MyMapLayerPicker (eye action lives in its actions column now)
.MyMapOsmCategory__typeOuvrageWrapper {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  margin-bottom: 0.25rem;

  // When a classification drawer is present, fuse it with the layer row above into a single
  // bordered card (row on top, classification as its footer) so the drawer visually reads as
  // part of that type d'ouvrage instead of a separate, unrelated block.
  &:has(.MyMapOsmCategory__classification) {
    gap: 0;
    border: 1px solid rgba(var(--v-theme-main-blue), 0.3);
    border-radius: var(--dim-radius);
    overflow: hidden;

    .MyMapLayerPicker__parentBlock {
      border: none;
      border-radius: 0;
    }
  }
}

.MyMapOsmCategory__infraMapEyeBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(var(--v-theme-main-blue), 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;

    &:hover {
      background: transparent;
    }
  }
}

.MyMapOsmCategory__classification {
  margin: 0;
  border: none;
  border-top: 1px solid rgba(var(--v-theme-main-blue), 0.3);
  background: rgba(var(--v-theme-main-blue), 0.045);
}

.MyMapOsmCategory__classificationList {
  display: flex;
  flex-flow: column nowrap;
  gap: 0.1rem;
  padding: 0.25rem 0.45rem;
}

.MyMapOsmCategory__classificationItem {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  font-size: var(--font-size-sm);
  padding: 0.4rem 0.5rem;
  border-radius: calc(var(--dim-radius) - 0.15rem);
  transition: background-color 0.12s ease;

  &:hover {
    background: rgba(var(--v-theme-main-blue), 0.08);
  }
}

.MyMapOsmCategory__classificationCheckbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: rgb(var(--v-theme-main-blue));
}

.MyMapOsmCategory__classificationSwatch {
  flex-shrink: 0;
  width: 0.85rem;
  height: 0.85rem;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}

.MyMapOsmCategory__classificationLabel {
  flex: 1;
  word-break: break-word;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.MyMapOsmCategory__classificationCount {
  flex-shrink: 0;
  min-width: 1.4rem;
  text-align: center;
  border-radius: 999px;
  background: rgba(var(--v-theme-main-blue), 0.1);
  color: rgba(var(--v-theme-main-blue), 0.75);
  padding: 0.06rem 0.45rem;
  font-size: 0.7rem;
  font-weight: 700;
}

</style>
